import React, { useEffect, useState } from 'react';
import { FiMapPin, FiVideo, FiCalendar, FiClock, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import DashboardShell from '../../components/DashboardShell/DashboardShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import RequestMeetingModal from '../../components/RequestMeetingModal/RequestMeetingModal';
import WalletBadge from '../../components/WalletBadge/WalletBadge';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { fetchMyLeads } from '../../features/leads/leadsSlice';

const STATUS_COLORS: Record<string, string> = { pending: '#e8a86a', confirmed: '#1d60f6', rejected: '#e2534a' };

const Meetings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { myLeads, status } = useAppSelector((state) => state.leads);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => { dispatch(fetchMyLeads()); }, [dispatch]);

  const meetings = myLeads.filter((l) => l.lead_type === 'meeting');

  return (
    <DashboardShell>
      <PageHeader
        title="Meetings"
        subtitle="Request a meeting with our team, and track your past requests here."
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <WalletBadge />
            <button
              onClick={() => setShowModal(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'linear-gradient(120deg,var(--copper),var(--copper-light))', color: '#ffffff',
                fontWeight: 700, fontSize: 13, padding: '11px 18px', borderRadius: 10, border: 'none', cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(29,96,246,.22)',
              }}
            >
              <FiCalendar /> Request a Meeting
            </button>
          </div>
        }
      />

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '16px 16px 60px' }}>
        {status === 'loading' && <div style={{ color: 'var(--muted)', padding: 40, textAlign: 'center' }}>Loading…</div>}
        {status !== 'loading' && meetings.length === 0 && (
          <div style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center', padding: '50px 20px', background: 'var(--card-bg)', border: '1px solid var(--line)', borderRadius: 16 }}>
            No meeting requests yet. Use "Request a Meeting" above to get started.
          </div>
        )}
        {status !== 'loading' && meetings.map((m) => (
          <div key={m.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--line)', borderRadius: 14, padding: '18px 20px', marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <b style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--cream)' }}>
                {m.meeting_type === 'vc' ? <FiVideo /> : <FiMapPin />}
                {m.meeting_type === 'vc' ? 'Virtual / VC call' : 'In-person meeting'}
              </b>
              <span
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
                  color: STATUS_COLORS[m.status], border: `1px solid ${STATUS_COLORS[m.status]}`,
                  borderRadius: 20, padding: '4px 12px', background: `${STATUS_COLORS[m.status]}1a`,
                }}
              >
                {m.status === 'confirmed' ? <FiCheckCircle /> : m.status === 'rejected' ? <FiXCircle /> : <FiClock />}
                {m.status}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
              Requested {new Date(m.created_at).toLocaleString()}
            </div>
            {m.message && <p style={{ fontSize: 13, marginTop: 10, color: 'var(--muted)' }}>{m.message}</p>}
            {m.status === 'confirmed' && m.contact_by && (
              <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--copper-light)' }}>
                Our team will contact you by {new Date(m.contact_by).toLocaleString()}
              </div>
            )}
          </div>
        ))}
      </div>

      {showModal && (
        <RequestMeetingModal
          onClose={() => setShowModal(false)}
          onSuccess={() => dispatch(fetchMyLeads())}
        />
      )}
    </DashboardShell>
  );
};

export default Meetings;
